// Firebase auth error codes, narrowed to the handful of things that actually
// went wrong from the user's point of view.
//
// Pure and free of React so it can be reasoned about — and tested — on its own;
// how each kind is presented is authErrorView's problem.

export function classifyError(err) {
  switch (err.code || '') {
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      // Firebase collapses "no such user" and "wrong password" into one code
      // so an attacker cannot enumerate accounts; they are presented as one.
      return { kind: 'wrongCreds' };
    case 'auth/email-already-in-use':
      return { kind: 'emailTaken' };
    case 'auth/weak-password':
      return { kind: 'weakPassword' };
    case 'auth/invalid-email':
      return { kind: 'invalidEmail' };
    case 'auth/too-many-requests':
      return { kind: 'tooMany' };
    case 'auth/missing-email':
      return { kind: 'missingEmail' };
    case 'auth/network-request-failed':
      return { kind: 'banner', text: 'Network is offline. Check your connection and try again.' };

    // ── Google (#567) ─────────────────────────────────────────────────────
    //
    // The teacher closed the Google window, or pressed the button again while
    // one was open. They did that on purpose, so nothing is said.
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/user-cancelled':
      return { kind: 'dismissed' };
    // The address already has an account that signs in some other way — a
    // password — and the project allows one account per address. Firebase
    // says this only when Google does not own the address: for an @gmail.com
    // address Google is trusted, and Firebase signs in to the existing
    // account instead. If that account's address was never confirmed,
    // Firebase also drops its password: a password nobody proved loses to an
    // address Google has just proved. The uid, and so the studio, is kept, and
    // a teacher who tries the old password is told to use Google
    // (authErrorView `wrongCreds`). That is Firebase's, not a choice made here;
    // test/integration/signInGate.test.jsx pins it in the emulator.
    // `customData.email` is the address, so the form can fill it in.
    case 'auth/account-exists-with-different-credential':
      return { kind: 'googleAccountExists', email: err.customData?.email || null };
    // The Google provider is not switched on in the Firebase console, or this
    // site's address is not one of the project's authorised domains. Both are
    // project configuration (#517), not the teacher's to fix.
    case 'auth/operation-not-allowed':
    case 'auth/unauthorized-domain':
    case 'auth/admin-restricted-operation':
      return { kind: 'googleUnavailable' };
    // The pop-up was blocked and the full-page redirect could not be used
    // either. AuthContext tries the redirect first, so this is what is left.
    case 'auth/popup-blocked':
    case 'auth/operation-not-supported-in-this-environment':
      return { kind: 'popupBlocked' };
    default:
      return { kind: 'banner', text: err.message || 'Something went wrong. Please try again.' };
  }
}

/**
 * Whether a failed Google pop-up should be retried as a full-page redirect.
 *
 * Only when the pop-up could not open at all. A pop-up the teacher closed is a
 * decision, and taking them away from the page after it would overrule it.
 */
export function googleNeedsRedirect(err) {
  const code = err?.code || '';
  return code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment';
}

// ── A password tried on an account that signs in with Google (#591) ────────
//
// A teacher who signed up with an address and a password, never confirmed it,
// and later signed in with Google, no longer has a password: Firebase dropped
// it and kept the account, and its studio, for Google (see
// `auth/account-exists-with-different-credential` above). Their next password
// attempt says so (#591), rather than "Email or password didn't match".
//
// Firebase's refusal cannot tell this apart from a mistyped password: both are
// `auth/wrong-password`, or `auth/invalid-credential` with email enumeration
// protection on. So after a wrong-credentials refusal the form asks Firebase
// which ways the address signs in (`fetchSignInMethodsForEmail`), and this
// reads the answer.
//
// With enumeration protection on (#445) that answer is always empty, so this
// says no and the form keeps the generic message, which points at Continue
// with Google.

/**
 * Whether the sign-in methods Firebase reported for an address are Google and
 * nothing a password form could use. Empty, unknown or missing is a no.
 */
export function signsInOnlyWithGoogle(methods) {
  if (!Array.isArray(methods) || !methods.includes('google.com')) return false;
  return !methods.includes('password') && !methods.includes('emailLink');
}

/**
 * What a failed email-and-password sign-in means, given what Firebase then
 * said about the address. `methods` is null when it was not asked or could
 * not answer. Only a wrong-credentials refusal is ever re-read.
 */
export function classifyPasswordSignIn(err, methods) {
  const classified = classifyError(err);
  if (classified.kind === 'wrongCreds' && signsInOnlyWithGoogle(methods)) return { kind: 'googleOnly' };
  return classified;
}
